import { Component, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Base component that provides automatic subscription cleanup
 * 
 * Usage Option 1 (Recommended - No pipe needed):
 * ```typescript
 * export class MyComponent extends BaseComponent implements OnInit {
 *   ngOnInit() {
 *     this.subscribe(
 *       this.myService.getData(),
 *       data => {
 *         // Your logic here
 *       }
 *     );
 *   }
 * }
 * ```
 * 
 * Usage Option 2 (With pipe for custom operators):
 * ```typescript
 * export class MyComponent extends BaseComponent implements OnInit {
 *   ngOnInit() {
 *     this.myService.getData()
 *       .pipe(
 *         map(data => data.items),
 *         this.untilDestroyed()
 *       )
 *       .subscribe(items => {
 *         // Your logic here
 *       });
 *   }
 * }
 * ```
 */
@Component({
    template: '',
    standalone: true
})
export abstract class BaseComponent implements OnDestroy {
    private readonly destroy$ = new Subject<void>();

    /**
     * Subscribe to an observable with automatic cleanup
     * No need to use pipe or takeUntil - just subscribe!
     * 
     * @param observable The observable to subscribe to
     * @param next Callback for next value
     * @param error Optional error callback
     * @param complete Optional complete callback
     * @returns Subscription (automatically unsubscribed on destroy)
     */
    protected subscribe<T>(
        observable: Observable<T>,
        next: (value: T) => void,
        error?: (error: any) => void,
        complete?: () => void
    ): Subscription {
        return observable
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next,
                error,
                complete
            });
    }

    /**
     * Returns an RxJS operator that automatically unsubscribes when component is destroyed
     * Use this in pipe() when you need custom operators before subscribing
     */
    protected untilDestroyed<T>() {
        return (source: Observable<T>) => source.pipe(takeUntil(this.destroy$));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

